import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Input } from "@shared-ui/input";
import { Shield } from "lucide-react";
import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { NOT_ADMIN_REASON } from "@/lib/non-admin-session";
import { useAdminAuthStore } from "../store/admin-auth.store";

export function AdminLoginPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const isFileProtocol =
		typeof window !== "undefined" && window.location.protocol === "file:";
	const {
		operator,
		error,
		isLoading,
		clearError,
		clearSession,
		hasStoredSession,
		resetOtpState,
		sendOtp,
	} = useAdminAuthStore();
	const hasSession = hasStoredSession();
	const signedOutAsNonAdmin =
		new URLSearchParams(location.search).get("reason") === NOT_ADMIN_REASON;
	// Kept in state, because the effect below takes the reason out of the URL.
	const [showNotAdminNotice] = useState(signedOutAsNonAdmin);
	const [phone, setPhone] = useState(
		((location.state as { phone?: string } | null)?.phone ?? "")
			.replace(/\D/g, "")
			.slice(-10),
	);

	useEffect(() => {
		if (operator && !hasSession) {
			clearSession();
		}
	}, [clearSession, hasSession, operator]);

	// Sent here because the console's session holds no admin role any more
	// (see lib/non-admin-session.ts). Drop the stored operator and session marker,
	// so the console stops treating that session as an admin's, then take the
	// reason out of the history entry: otherwise Back, after signing in
	// again, would land here with it and sign the new session out.
	useEffect(() => {
		if (!signedOutAsNonAdmin) return;
		clearSession();
		navigate("/login", { replace: true });
	}, [clearSession, navigate, signedOutAsNonAdmin]);

	const handleContinue = async () => {
		const cleanPhone = phone.replace(/\D/g, "").slice(-10);
		if (cleanPhone.length !== 10) {
			return;
		}

		const state = location.state as { from?: string } | null;
		resetOtpState();
		try {
			await sendOtp(cleanPhone);
			navigate("/verify-otp", {
				state: {
					phone: cleanPhone,
					from: state?.from,
				},
			});
		} catch {
			// `sendOtp` already set `error` in the store; the banner below
			// renders it. Stay on this page rather than navigating to a
			// verify screen with no OTP behind it.
		}
	};

	if (operator && hasSession && !signedOutAsNonAdmin) {
		return <Navigate to="/payments" replace />;
	}

	return (
		<div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[radial-gradient(circle_at_top,_rgba(245,158,11,0.18),_transparent_32%),linear-gradient(180deg,_#fcfcfd,_#f3f4f6)] px-4 py-10">
			<div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-300 to-transparent" />
			<Card className="w-full max-w-md border-white/70 bg-white/90 shadow-2xl shadow-slate-200/70 backdrop-blur">
				<CardHeader className="gap-3">
					<div className="flex size-12 items-center justify-center rounded-2xl bg-slate-950 text-white">
						<Shield className="size-5" />
					</div>
					<div className="space-y-1">
						<CardTitle className="text-2xl">Platform admin</CardTitle>
						<CardDescription>
							Use your existing OTP flow, then this console will verify admin
							allowlist access before loading operations.
						</CardDescription>
					</div>
				</CardHeader>
				<CardContent className="space-y-6">
					{showNotAdminNotice ? (
						<div
							role="status"
							className="space-y-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
						>
							<p className="font-medium">
								You were signed out of the admin console.
							</p>
							<p>This account no longer has admin access.</p>
							<p>
								Sign in below with an account that has admin access. If this
								account should have it, ask an admin who manages roles to grant
								it a role again.
							</p>
						</div>
					) : null}
					{isFileProtocol ? (
						<div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
							This page is open from{" "}
							<span className="font-medium">file://</span>. Serve the admin app
							through Vite for the cleanest local flow, or make sure the backend
							is running on{" "}
							<span className="font-medium">http://localhost:3000</span>.
						</div>
					) : null}

					<div className="grid gap-3">
						<label
							htmlFor="admin-login-phone"
							className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground"
						>
							Phone
						</label>
						<Input
							id="admin-login-phone"
							value={phone}
							onChange={(event) => {
								clearError();
								setPhone(event.target.value.replace(/\D/g, "").slice(-10));
							}}
							placeholder="9876543210"
							disabled={isLoading}
						/>
						<Button
							onClick={() => void handleContinue()}
							disabled={phone.length !== 10 || isLoading}
						>
							Continue
						</Button>
					</div>

					{error ? (
						<div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
							<div className="flex items-start justify-between gap-3">
								<span>{error}</span>
								<button
									type="button"
									onClick={clearError}
									className="font-medium text-rose-700"
								>
									Clear
								</button>
							</div>
						</div>
					) : null}
				</CardContent>
			</Card>
		</div>
	);
}
